import React, { useEffect } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import './UrgentBroadcastModal.css';

interface Broadcast {
  id: number;
  title: string;
  content: string;
  sender_name?: string;
  sender_alias?: string;
  created_at: string;
}

interface UrgentBroadcastModalProps {
  broadcast: Broadcast | null;
  onConfirm: (id: number) => void;
}

const UrgentBroadcastModal: React.FC<UrgentBroadcastModalProps> = ({ broadcast, onConfirm }) => {
  useEffect(() => {
    // Prevent body scroll when modal is open
    if (broadcast) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [broadcast]);

  useEffect(() => {
    // Close on Escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && broadcast) {
        handleConfirm();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [broadcast]);

  const handleConfirm = () => {
    if (!broadcast) return;
    onConfirm(broadcast.id);
  };

  if (!broadcast) return null;

  const senderDisplay = broadcast.sender_alias || broadcast.sender_name || 'Sistema';
  const formattedDate = new Date(broadcast.created_at).toLocaleString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <div className="urgent-broadcast-overlay" onClick={handleConfirm}>
      <div className="urgent-broadcast-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close button (disabled in urgent mode) */}
        <button 
          className="urgent-modal-close" 
          onClick={handleConfirm}
          title="Presiona Escape o haz clic en Confirmar"
          disabled
        >
          <X size={20} />
        </button>

        {/* Icon and header */}
        <div className="urgent-modal-header">
          <div className="urgent-icon-wrapper">
            <AlertTriangle size={32} className="urgent-icon" />
          </div>
          <div className="urgent-header-text">
            <h2>{broadcast.title}</h2>
            <span className="urgent-badge">MENSAJE URGENTE</span>
          </div>
        </div>

        {/* Content */}
        <div className="urgent-modal-content">
          <p className="urgent-message">{broadcast.content}</p>
        </div>

        {/* Footer */}
        <div className="urgent-modal-footer">
          <div className="urgent-footer-info">
            <span className="urgent-sender">De: {senderDisplay}</span>
            <span className="urgent-time">{formattedDate}</span>
          </div>
          <button className="urgent-confirm-btn" onClick={handleConfirm}>
            Confirmar Lectura
          </button>
        </div>
      </div>
    </div>
  );
};

// Global instance for WebSocket to trigger urgent broadcasts
let urgentBroadcastInstance: {
  show: (broadcast: Broadcast) => void;
} | null = null;

export const UrgentBroadcastController = () => {
  const [broadcast, setBroadcast] = React.useState<Broadcast | null>(null);

  React.useEffect(() => {
    urgentBroadcastInstance = {
      show: (newBroadcast: Broadcast) => {
        setBroadcast(newBroadcast);
      }
    };

    // Expose globally for WebSocket
    (window as any).urgentBroadcast = {
      show: (newBroadcast: Broadcast) => {
        urgentBroadcastInstance?.show(newBroadcast);
      }
    };

    return () => {
      urgentBroadcastInstance = null;
      delete (window as any).urgentBroadcast;
    };
  }, []);

  const handleConfirm = async (id: number) => {
    try {
      const response = await fetch(`/api/broadcast/${id}/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        setBroadcast(null);
      } else {
        console.error('Failed to confirm broadcast');
        // Still close the modal
        setBroadcast(null);
      }
    } catch (error) {
      console.error('Error confirming broadcast:', error);
      // Still close the modal
      setBroadcast(null);
    }
  };

  return <UrgentBroadcastModal broadcast={broadcast} onConfirm={handleConfirm} />;
};

export default UrgentBroadcastModal;
